import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { COVERAGE_DATA } from "constants/insurance";

// ===== Styled Components =====

const PageWrapper = styled.div`
  min-height: 100vh;
  background: #f5f7fa;
  padding: 16px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
`;

const Header = styled.header`
  text-align: center;
  margin-bottom: 20px;
`;

const Title = styled.h1`
  font-size: 20px;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0 0 4px 0;
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: #666;
  margin: 0;
`;

const CardContainer = styled.div`
  background: #ffffff;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
`;

const CardTitle = styled.h2`
  font-size: 15px;
  font-weight: 600;
  color: #1a1a2e;
  margin: 0 0 4px 0;
`;

const CardTitleTh = styled.p`
  font-size: 13px;
  color: #666;
  margin: 0 0 12px 0;
`;

const CoverageAmount = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: #2d6a4f;
`;

const SubCoverageList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const SubCoverageItem = styled.li`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }
`;

const SubCoverageLabel = styled.span`
  font-size: 13px;
  color: #333;
  flex: 1;
`;

const SubCoverageAmount = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #2d6a4f;
  text-align: right;
  white-space: nowrap;
  margin-left: 8px;
`;

const SummaryCard = styled.div`
  background: #ffffff;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
`;

const PremiumRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const PremiumLabel = styled.span`
  font-size: 14px;
  color: #333;
`;

const PremiumValue = styled.span`
  font-size: 20px;
  font-weight: 700;
  color: #1a1a2e;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  border-top: 1px solid #f0f0f0;
`;

const InfoLabel = styled.span`
  font-size: 13px;
  color: #666;
`;

const InfoValue = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: #333;
`;

const BuyButtonStyled = styled.button`
  display: block;
  width: 100%;
  min-height: 48px;
  min-width: 48px;
  padding: 14px 24px;
  background: #2d6a4f;
  color: #ffffff;
  font-size: 16px;
  font-weight: 600;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  margin-top: 8px;
  transition: background 0.2s ease;

  &:hover {
    background: #1b4332;
  }

  &:active {
    background: #143d2b;
  }
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  padding: 24px;
  text-align: center;
`;

const ErrorMessage = styled.p`
  font-size: 16px;
  color: #d32f2f;
  margin: 0 0 16px 0;
`;

const RetryButton = styled.button`
  min-height: 44px;
  min-width: 44px;
  padding: 12px 24px;
  background: #2d6a4f;
  color: #ffffff;
  font-size: 14px;
  font-weight: 500;
  border: none;
  border-radius: 8px;
  cursor: pointer;

  &:hover {
    background: #1b4332;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  padding: 24px;
`;

const LoadingText = styled.p`
  font-size: 14px;
  color: #666;
`;

// ===== Sub-components =====

function formatAmount(amount, unit) {
  const formattedAmount = amount.toLocaleString();
  return `${formattedAmount} ${unit}`;
}

function CoverageCard({ section }) {
  return (
    <CardContainer>
      <CardTitle>{section.title}</CardTitle>
      <CardTitleTh>{section.titleTh}</CardTitleTh>
      {section.amount && (
        <CoverageAmount>{formatAmount(section.amount, section.unit)}</CoverageAmount>
      )}
      {section.subCoverages && (
        <SubCoverageList>
          {section.subCoverages.map((sub) => (
            <SubCoverageItem key={sub.id}>
              <SubCoverageLabel>{sub.label}</SubCoverageLabel>
              <SubCoverageAmount>
                {formatAmount(sub.amount, sub.unit)}
                {sub.maxWeeks && ` (max ${sub.maxWeeks} weeks)`}
              </SubCoverageAmount>
            </SubCoverageItem>
          ))}
        </SubCoverageList>
      )}
    </CardContainer>
  );
}

function PremiumSummary({ premium, premiumCurrency, premiumInclusive, territorialLimit, underwriter }) {
  return (
    <SummaryCard>
      <PremiumRow>
        <PremiumLabel>เบี้ยประกันภัยรวม (Total Premium)</PremiumLabel>
        <PremiumValue>
          {premium.toLocaleString()} {premiumCurrency}
        </PremiumValue>
      </PremiumRow>
      <InfoRow>
        <InfoLabel>รวมภาษี ({premiumInclusive})</InfoLabel>
        <InfoValue>Inclusive</InfoValue>
      </InfoRow>
      <InfoRow>
        <InfoLabel>ขอบเขตอาณาเขต (Territorial Limit)</InfoLabel>
        <InfoValue>{territorialLimit}</InfoValue>
      </InfoRow>
      <InfoRow>
        <InfoLabel>ผู้รับประกันภัย (Underwriter)</InfoLabel>
        <InfoValue>{underwriter}</InfoValue>
      </InfoRow>
    </SummaryCard>
  );
}

function BuyButton({ onClick }) {
  return (
    <BuyButtonStyled onClick={onClick} aria-label="ซื้อประกันภัย">
      ซื้อประกันภัย
    </BuyButtonStyled>
  );
}

// ===== Main Container =====

export default function InsuranceLandingPage({ initialError = false }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(initialError);
  const [data, setData] = useState(COVERAGE_DATA);

  const handleBuyClick = useCallback(() => {
    navigate("/insurance/apply");
  }, [navigate]);

  const handleRetry = useCallback(() => {
    setError(false);
    setLoading(true);
    // Simulate content reload - in a real scenario this would be an API call
    try {
      setData(COVERAGE_DATA);
      setLoading(false);
    } catch (e) {
      setError(true);
      setLoading(false);
    }
  }, []);

  if (loading) {
    return (
      <PageWrapper>
        <LoadingContainer>
          <LoadingText>กำลังโหลด...</LoadingText>
        </LoadingContainer>
      </PageWrapper>
    );
  }

  if (error) {
    return (
      <PageWrapper>
        <ErrorContainer>
          <ErrorMessage>ไม่สามารถโหลดข้อมูลได้ กรุณาลองใหม่อีกครั้ง</ErrorMessage>
          <RetryButton onClick={handleRetry}>ลองใหม่</RetryButton>
        </ErrorContainer>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <Header>
        <Title>ประกันภัยกอล์ฟ</Title>
        <Subtitle>Golfer Insurance</Subtitle>
      </Header>

      {data.sections.map((section) => (
        <CoverageCard key={section.id} section={section} />
      ))}

      <PremiumSummary
        premium={data.premium}
        premiumCurrency={data.premiumCurrency}
        premiumInclusive={data.premiumInclusive}
        territorialLimit={data.territorialLimit}
        underwriter={data.underwriter}
      />

      <BuyButton onClick={handleBuyClick} />
    </PageWrapper>
  );
}
